import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Plus from "@/assets/plusSign.svg";
import Minus from "@/assets/minusSign.svg";
import Tooltip from "../Tooltip";

interface InputCounterProps {
  initialValue: number;
  min: number;
  max?: number;
  step: number;
  label: string;
  name: string;
  onChange?: (name: string, value: number) => void;
  isLong?: boolean;
  prefix?: string;
  suffix?: string;
  disabled?: boolean;
  onBlur?: (value: number) => void;
  showToolTip?: boolean;
  tooltipText?: string;
}
const InputCounter: React.FC<InputCounterProps> = ({
  initialValue,
  min,
  max = 1000000,
  step,
  label,
  name,
  onChange,
  isLong,
  prefix,
  suffix,
  disabled,
  onBlur,
  showToolTip,
  tooltipText,
}) => {
  const [count, setCount] = useState(initialValue);
  const [inputValue, setInputValue] = useState(formatValue(initialValue));
  const inputRef = useRef<HTMLInputElement>(null);
  const mirrorRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!inputRef.current) return;
    if (suffix && mirrorRef.current) {
      inputRef.current.style.width = mirrorRef.current.offsetWidth + "px";
      return;
    }
    inputRef.current.style.width = "";
  }, [inputValue, suffix]);

  // Format the value to have max 2 decimal places
  function formatValue(value: number): string {
    const rounded = Math.round(value * 100) / 100;

    return rounded.toString();
  }

  useEffect(() => {
    setInputValue(formatValue(count));
  }, [count]);

  useEffect(() => {
    setCount(initialValue);
    setInputValue(formatValue(initialValue));
  }, [initialValue]);

  const increment = () => {
    if (count < max) {
      const newValue = count + step >= 0 ? count + step : 0;
      // Ensure the new value doesn't exceed max
      const constrainedValue = Math.min(newValue, max);
      const roundedValue = parseFloat(constrainedValue.toFixed(2));

      setCount(roundedValue);
      if (onChange) {
        onChange(name, roundedValue);
      }
    }
  };

  const decrement = () => {
    if (count > min && count - step >= 0) {
      const newValue = count - step;

      const roundedValue = parseFloat(newValue.toFixed(2));
      setCount(roundedValue);
      if (onChange) {
        onChange(name, roundedValue);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const decimalRegex = /^-?\d*\.?\d{0,2}$/;

    if (value === "" || decimalRegex.test(value)) {
      const numericValue = parseFloat(value);

      // If the value is empty, allow it for user experience
      if (value === "") {
        setInputValue(value);
        return;
      }

      // If numeric value is valid and within bounds, update both display and count
      if (!isNaN(numericValue) && numericValue >= min && numericValue >= 0) {
        if (numericValue <= max) {
          setInputValue(value);
          setCount(numericValue);
          if (onChange) {
            onChange(name, numericValue);
          }
        } else {
          // Don't allow input that exceeds max - keep the previous valid value
          setInputValue(formatValue(count));
        }
      } else {
        // Don't allow invalid input - keep the previous valid value
        setInputValue(formatValue(count));
      }
    }
  };

  const handleBlur = () => {
    let numericValue = parseFloat(inputValue);

    if (isNaN(numericValue) || numericValue < min) {
      numericValue = min;
    } else if (numericValue > max) {
      numericValue = max;
    }

    const roundedValue = parseFloat(numericValue.toFixed(2));
    setCount(roundedValue);
    setInputValue(formatValue(roundedValue));

    if (onChange) {
      onChange(name, roundedValue);
    }

    if (onBlur) {
      onBlur(roundedValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "-" || event.key === "e") {
      event.preventDefault();
    }
  };

  return (
    <>
      <div className="flex w-full rounded-[8px] border border-[#E2E2E2]">
        <div className="w-[56px] h-[56px] rounded-[8px] ">
          <button
            onClick={decrement}
            disabled={disabled || count <= min}
            className="bg-white px-[18px] h-full rounded-l-[8px]"
          >
            <img
              src={Minus}
              className="w-[21px] h-[21px] object-contain"
              alt="Minus sign"
            />
          </button>
        </div>

        <div
          style={{ boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.07) inset" }}
          className="flex-1 flex-col h-[56px] bg-[#F8F8F8] p-[8px] border-x border-[#E2E2E2] cursor-text"
          onClick={() => inputRef.current?.focus()}
        >
          <div className="flex items-center space-x-1">
            <p className="relative flex text-[#A7A7A7] text-[14px] leading-[127%] ">
              {label}
              <span className="text-[18px] ml-1">
                {showToolTip && tooltipText && <Tooltip text={tooltipText} />}
              </span>
            </p>
          </div>
          <div className="flex items-center">
            {prefix && (
              <span className="text-[#A7A7A7] text-[18px] mr-1">{prefix}</span>
            )}
            {suffix && (
              <span
                ref={mirrorRef}
                aria-hidden
                style={{
                  position: "absolute",
                  visibility: "hidden",
                  whiteSpace: "pre",
                  pointerEvents: "none",
                  fontFamily: "inherit",
                  fontSize: "inherit",
                }}
              >
                {inputValue || "0"}
              </span>
            )}
            <input
              ref={inputRef}
              aria-label={name}
              id={name}
              name={name}
              type="text"
              className={`bg-[#F8F8F8] outline-none ${
                suffix ? "" : isLong ? "w-full" : "w-[70px]"
              }`}
              value={inputValue}
              onChange={handleChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              disabled={disabled}
            />
            {suffix && inputValue && (
              <span
                className="text-black text-[18px] cursor-text select-none"
                onClick={() => inputRef.current?.focus()}
              >
                {suffix}
              </span>
            )}
          </div>
        </div>

        <div className="w-[56px] h-[56px] rounded-[8px]">
          <button
            onClick={increment}
            className="bg-white px-[18px] h-full rounded-r-[8px]"
            disabled={disabled || count >= max}
          >
            <img
              src={Plus}
              className="w-[21px] h-[21px] object-contain"
              alt="Plus sign"
            />
          </button>
        </div>
      </div>
    </>
  );
};

export default InputCounter;
